import { ApolloQueryResult } from "@apollo/client";
import axios, { AxiosResponse } from "axios";
import { SERVER_URL } from "Constants";
import gql from "graphql-tag";
import { AddressEntity, CustomerEntity, GroupEntity, GroupsCustomers } from "Models/Entities";
import BusinessDetailEntity from "Models/Entities/BusinessDetailEntity";
import { customerMonitoredStatus } from "Models/Enums";
import { store } from "Models/Store";
import { AxiosConfigFactory } from "Util/AxiosUtil";
import { RegistrationSummary } from "Views/Components/Intel/Customer/PpsrRegistrationTypes";
import { EntityControllerData, PaginationParams, SearchParams, SortingParams } from "./shared";

export type AddPaygCustomerRequest = {
	customerId: string;
	businessId: string;
	monitorPricePaygAmountId: string;
}

export const EmptyCustomerSummary: CustomerSummary = {
	currentValue: 0,
	maxValue: 0,
}

export interface CustomerSummary {
	currentValue: number;
	maxValue: number;
}

export interface BusinessEntityPpsrSummary {
	ppsrSummary?: RegistrationSummary,
	errorMessage: string,
	errors: {
		[key: string]: string,
	},
}

export type ApproveSummary =  {
	notSubmitted: number,
	referred: number,
	pending: number,
	declined: number,
	approved: number,
	notStarted: number,
}

export interface BusinessEntityApproveSummary {
	approveSummary?: ApproveSummary,
	errorMessage: string,
	errors: {
		[key: string]: string,
	},
}

export async function apiAddPaygCustomer(request: AddPaygCustomerRequest) {

	const url = `${SERVER_URL}/api/entity/CustomerEntity/add-payg`;
	return await axios.post<object>(url, request);
}

export async function apiUpdateCustomerMonitoredStatus(customerId: string, monitoredStatus: customerMonitoredStatus) {
	const url = `${SERVER_URL}/api/entity/CustomerEntity/monitored-status/${customerId}/${monitoredStatus}`;
	return await axios.post<object>(url);
}

export async function getOrganisationApproveSummary() {
	const url = `${SERVER_URL}/api/entity/CustomerEntity/business-entities/approve-summary`;
	return await axios.get<BusinessEntityApproveSummary>(url);
}


export async function getOrganisationCustomerSummary() {
	const url = `${SERVER_URL}/api/entity/CustomerEntity/customers-summary`;
	return await axios.get<CustomerSummary>(url);
}


export async function getOrganisationPpsrSummary() {
	const url = `${SERVER_URL}/api/entity/CustomerEntity/business-entities/ppsr-summary`;
	return await axios.get<BusinessEntityPpsrSummary>(url);
}

export type CustomerBusinessDetail = {
	customerId: string;
	abnAcnDetail: BusinessDetailEntity;
	trusteeDetail?: BusinessDetailEntity;
}

export async function getCustomerBusinessDetails(id: string) {
	const url = `${SERVER_URL}/api/entity/CustomerEntity/${id}/business-details`;
	return await axios.get<CustomerBusinessDetail>(url);
}

export type CustomerEntityQueryOptions = SortingParams & SearchParams & PaginationParams & {
	filterByOrganisationId?: string;
	filterByBusinessIds?: string[];
	filterByStatus?: customerMonitoredStatus[];
}

export async function getCustomers(queryParams?: CustomerEntityQueryOptions):Promise<AxiosResponse<EntityControllerData<CustomerEntity>>> {
	const url = `${SERVER_URL}/api/entity/CustomerEntity`;
	const requestConfig = AxiosConfigFactory.createRequestConfig(queryParams);
	return await axios.get<EntityControllerData<CustomerEntity>>(url, requestConfig);
}

export const fetchCustomerProfile = async (customerId?: string): Promise<ApolloQueryResult<{
	customerEntity?: CustomerEntity;
}>> => {
	const results = await store.apolloClient.query({
		query: gql`
					query fetchCustomerById($customerId: ID) {
						customerEntity(id: $customerId) {
							${CustomerEntity.getAllAttributes().join('\n')}
							paymentRiskRating
							businessEntity {
								id
								name
								enabledForApprove
								enabledForPPSR
								enableDrinksLicence
								organisation {
									id
									ppsrEnabled
									approveEnabled
									accountType
								}
							}
							physicalAddress {
								${AddressEntity.getAllAttributes().join('\n')}
							}
							postalAddress {
								${AddressEntity.getAllAttributes().join('\n')}
							}
							groupss {
								${GroupsCustomers.getAllAttributes().join('\n')}
								groups {
									${GroupEntity.getAllAttributes().join('\n')}
								}
							}
							currentAtbRecord {
								id
								totalOverdue
								totalDebt
							}
							newestAlares {
								id
								insolvencyRiskRating
								adverseAlerts
								# jsonreport - Don't include this - it's too big
							}
						}
					}`,
		variables: {
			customerId: customerId,
		},
		fetchPolicy: 'no-cache',
	});
	return results;
}